import {
  addMonths,
  Card,
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  formatDate,
  Column,
  getLocalStorageItem,
  NotApplicable,
  removeLocalStorageItem,
  Row,
  Select,
  setLocalStorageItem,
  useLoading,
  ValueOpt,
  StatsTeam,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAdminSuiteRequests } from "../../../api/RequesTixApi";
import AdminSuiteRequestGame from "../../../components/requests/adminSuite/AdminSuiteRequestGame";
import LocalStorageConstants from "../../../constants/LocalStorageConstants";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { GamePreviewDTO } from "../../../types/Game";
import { AdminSuiteRequestsDTO, SuiteRequestDTO, SuiteRequestStatus } from "../../../types/SuiteRequest";
import { useUrlParams } from "../../../util/RouteUtil";
import { getTeamOption } from "../../../util/TeamUtil";

const today = new Date();
const nextMonth = addMonths(today, 1);
const todayStr = formatDate(today, DATE_FORMATTER_STR);
const nextMonthStr = formatDate(nextMonth, DATE_FORMATTER_STR);

const AdminSuiteRequests = () => {
  const { setLoading } = useLoading();
  const { teamsOptions, suiteRequestStatusOptions } = useDropdowns();
  const [adminSuiteRequests, setAdminSuiteRequests] = useState<AdminSuiteRequestsDTO | undefined>(undefined);

  const localStorageHomeTeamId =
    (getLocalStorageItem(LocalStorageConstants.ADMIN_SUITE_REQUESTS_HOME_TEAM_ID) as string | null) ?? undefined;

  const [{ startDate, endDate, homeTeamId, statusId }, setParam] = useUrlParams({
    startDate: todayStr,
    endDate: nextMonthStr,
    homeTeamId: localStorageHomeTeamId,
    statusId: undefined,
  });

  const homeTeam: ValueOpt<StatsTeam> | undefined = useMemo(
    () => getTeamOption(homeTeamId, teamsOptions),
    [homeTeamId, teamsOptions],
  );
  const suiteStatus: ValueOpt<SuiteRequestStatus> | undefined = statusId
    ? suiteRequestStatusOptions.find(
        (opt: ValueOpt<SuiteRequestStatus>) => opt.value.suiteStatusId === parseInt(statusId),
      )
    : undefined;

  const dateRange: DateRange<Date> = {
    start: new Date(Date.parse(startDate!)),
    end: new Date(Date.parse(endDate!)),
  };

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res: AdminSuiteRequestsDTO = await fetchAdminSuiteRequests(
        startDate!,
        endDate!,
        homeTeamId ? Number(homeTeamId) : undefined,
        statusId ? Number(statusId) : undefined,
      );
      setAdminSuiteRequests(res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, homeTeamId, statusId, setLoading]);

  const updateHomeTeamId = useCallback(
    (newHomeTeamId: string | undefined) => {
      if (newHomeTeamId) {
        setLocalStorageItem(LocalStorageConstants.ADMIN_SUITE_REQUESTS_HOME_TEAM_ID, newHomeTeamId);
      } else {
        removeLocalStorageItem(LocalStorageConstants.ADMIN_SUITE_REQUESTS_HOME_TEAM_ID);
      }
      setParam("homeTeamId", newHomeTeamId);
    },
    [setParam],
  );

  const updateDate = useCallback(
    (dateRange: DateRange<Date> | NotApplicable) => {
      const dr = dateRange as DateRange<Date>;
      setParam({
        startDate: formatDate(dr.start, DATE_FORMATTER_STR),
        endDate: formatDate(dr.end, DATE_FORMATTER_STR),
      });
    },
    [setParam],
  );

  const refreshData = useCallback(() => {
    void fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    void fetchRequests();
  }, [fetchRequests]);

  return (
    <Card>
      <Card.Header>Suite Requests</Card.Header>
      <Card.Body>
        <Row className="bcr-mb-2">
          <Column width={4}>
            <DateRangeInput
              id="requests-date"
              label="Date"
              value={dateRange}
              onChange={updateDate}
              maxRange={{
                months: 1,
              }}
              withPortal
              gutterBottom
              popoverPlacement="bottom-end"
            />
          </Column>
          <Column width={4}>
            <Select
              id="home-team"
              label="Home Team"
              value={homeTeam}
              options={teamsOptions}
              onChange={(value: ValueOpt<StatsTeam>) =>
                updateHomeTeamId(value?.value?.id ? String(value.value.id) : undefined)
              }
              placeholder="select a home team"
              gutterBottom
            />
          </Column>
          <Column width={4}>
            <Select
              id="status"
              label="Status"
              value={suiteStatus}
              options={suiteRequestStatusOptions}
              onChange={(value: ValueOpt<SuiteRequestStatus>) =>
                setParam("statusId", value?.value?.suiteStatusId ? String(value.value.suiteStatusId) : undefined)
              }
              placeholder="Status"
              clearable
              gutterBottom
            />
          </Column>
        </Row>
        {!!adminSuiteRequests && (
          <>
            {adminSuiteRequests.schedule.map((gamePk: number) => {
              const game: GamePreviewDTO = adminSuiteRequests.games[gamePk];
              const requests: SuiteRequestDTO[] = adminSuiteRequests.requests[gamePk];
              if (!!game && !!requests) {
                return <AdminSuiteRequestGame key={gamePk} game={game} requests={requests} refreshData={refreshData} />;
              }
              return null;
            })}
          </>
        )}
      </Card.Body>
      <Card.Footer />
    </Card>
  );
};

export default AdminSuiteRequests;
